import { useCallback, useRef, useState } from 'react'

import { tusUploadFile } from '@/utils/tusUpload'

interface UploadRecord {
  file: File
  settled: boolean
  promise: Promise<string>
}

export interface EarlyUploadCallbacks {
  // Arquivo novo: o que se referia ao anterior deixa de valer.
  onNewUpload: () => void
  onUploading: () => void
  onProgress?: (percentage: number) => void
}

export interface EarlyUploadRequest {
  isCurrent: () => boolean
  cancel: () => void
}

/**
 * Upload antecipado para o anexo, comum à conferência do relatório de escolha
 * e à do mapa de atendimento: o arquivo sobe assim que é anexado, a conferência
 * usa o id e a resposta de uma conferência que deixou de ser a mais recente é
 * descartada. O que cada conferência faz com o resultado e com a falha fica no
 * hook dela.
 *
 * @param goIdle volta o estado do hook ao inicial; o `reset` chama. Precisa
 * ser estável (`useCallback`): o `reset` depende dele, e os efeitos dos hooks
 * dependem do `reset`.
 */
const useEarlyUpload = (goIdle: () => void) => {
  const [uploadId, setUploadId] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  // Guarda a *promessa* do upload, não só o id: re-rodar o efeito no meio do
  // envio (trocar a etapa, por exemplo) subiria o mesmo arquivo, que pode ter
  // centenas de MB, duas vezes.
  // `settled` diz se ela já terminou — é o que permite reexibir "enviando"
  // quando o efeito re-roda com o upload ainda em voo.
  const inFlightRef = useRef<UploadRecord | null>(null)
  // Duas conferências seguidas não podem deixar a resposta antiga sobrescrever
  // a nova.
  const requestSeqRef = useRef(0)

  const reset = useCallback(() => {
    requestSeqRef.current += 1
    inFlightRef.current = null
    setUploadId(previous => (previous === null ? previous : null))
    goIdle()
  }, [goIdle])

  const retry = useCallback(() => {
    setAttempt(previous => previous + 1)
  }, [])

  // `isCurrent` fica falso quando outra conferência começa ou quando o efeito
  // que abriu esta é limpo (`cancel`).
  const beginRequest = useCallback((): EarlyUploadRequest => {
    requestSeqRef.current += 1
    const seq = requestSeqRef.current
    let cancelled = false
    return {
      isCurrent: () => !cancelled && requestSeqRef.current === seq,
      cancel: () => {
        cancelled = true
      },
    }
  }, [])

  // O próximo efeito com este arquivo reenvia, em vez de reaproveitar o upload.
  const discardUpload = useCallback((file: File) => {
    if (inFlightRef.current?.file === file) {
      inFlightRef.current = null
    }
  }, [])

  /**
   * Sobe o arquivo, ou espera o upload que já está em voo para ele, e grava o
   * id. Devolve `null` se a conferência deixou de ser a atual no caminho.
   *
   * Falha do upload descarta a promessa guardada, para o retry reenviar, e
   * chega ao chamador como foi lançada.
   */
  const upload = useCallback(
    async (
      file: File,
      request: EarlyUploadRequest,
      callbacks: EarlyUploadCallbacks
    ): Promise<string | null> => {
      if (inFlightRef.current?.file !== file) {
        // O id antigo aponta para o arquivo anterior: se este upload falhar,
        // ninguém pode enviar o arquivo errado com ele.
        setUploadId(null)
        callbacks.onNewUpload()

        const promise = tusUploadFile(file, percentage => {
          if (inFlightRef.current?.file === file) {
            callbacks.onProgress?.(percentage)
          }
        })
        const record: UploadRecord = { file, settled: false, promise }
        // `then` com os dois handlers, e não `finally`: `finally` devolve uma
        // promessa derivada que rejeitaria sem ninguém escutando.
        promise.then(
          () => {
            record.settled = true
          },
          () => {
            record.settled = true
          }
        )

        inFlightRef.current = record
      }

      // Fora do `if` de propósito: re-rodar o efeito durante o envio não cria
      // upload novo, e sem isto a tela ficaria sem barra e sem explicação até
      // o upload acabar.
      if (!inFlightRef.current.settled) {
        callbacks.onUploading()
      }

      let currentUploadId: string
      try {
        currentUploadId = await inFlightRef.current.promise
      } catch (error) {
        discardUpload(file)
        throw error
      }

      if (!request.isCurrent()) return null

      setUploadId(currentUploadId)
      return currentUploadId
    },
    [discardUpload]
  )

  return {
    uploadId,
    attempt,
    retry,
    reset,
    beginRequest,
    discardUpload,
    upload,
  }
}

export default useEarlyUpload
